import { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { DismissableAppPromoBanner } from 'components/banners/app-promo-banner'
import { track, useTracking } from 'lib/analytics'
import { ProductCollection } from 'shared/product-collections'
import { CANONICAL_URL } from 'shared/constants'
import { StoreCategory } from 'src/types/storeCategory'
import { ProductCollection as ProductCollectionType } from 'src/types/productCollection'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import NiceModal from '@ebay/nice-modal-react'
import StoreHero from './hero'
import ContentContainer from '../content-container'
import CategoryGrid from '../CategoryGrid'
import WhyShopBabylist from './why-shop-babylist'
import { CreateBabylistCTA, FindBabylistCTA } from './call-to-action-box'
import Disclaimers from './disclaimers'
import { SubHeading } from '../../../components/headings'
import css from './landing-page.scss'
import RegistryDiscountBanner from '../../../components/banners/RegistryDiscountBanner'
import { CurrentUser } from '../../../global'
import RegistryDiscountStatusModal from '../../../registry/components/RegistryDiscountStatusV2/RegistryDiscountStatusModal'

interface LandingPageProps {
  showAppPromoBanner: boolean
  productCollections: ProductCollectionType[]
  storeNavigationCategories: StoreCategory[]
  storeRootCategories: StoreCategory[]
  currentUserCountry?: string
}

interface RenderBannerProps {
  currentUser: CurrentUser | undefined
  openDiscountModal: () => void
}

const RenderBanner = ({
  currentUser,
  openDiscountModal,
}: RenderBannerProps) => {
  const isEligibleForRegistryDiscount =
    currentUser?.currentRegistry?.eligibleForDiscount

  if (isEligibleForRegistryDiscount) {
    return <RegistryDiscountBanner openModal={openDiscountModal} />
  }

  return null
}

export const LandingPage = ({
  showAppPromoBanner,
  productCollections,
  storeNavigationCategories,
  storeRootCategories,
}: LandingPageProps) => {
  const tracker = useTracking()
  const [currentUser] = useCurrentUser()
  const registryDiscount = currentUser?.currentRegistry?.discountCohort

  useEffect(() => {
    tracker.trackEvent({
      event: track.storeLandingViewed,
      isRegistryOwner: !!currentUser?.registryId,
      registryOwnerId: currentUser?.id,
      registryId: currentUser?.registryId,
    })
  }, [])

  const [isRegistryDiscountModalOpen, setIsRegistryDiscountModalOpen] =
    useState(false)

  const handleDiscountModalClose = () => {
    setIsRegistryDiscountModalOpen(false)
  }

  const openDiscountModal = () => {
    setIsRegistryDiscountModalOpen(true)
  }

  return (
    <NiceModal.Provider>
      <div>
        {showAppPromoBanner && <DismissableAppPromoBanner />}

        <Helmet
          link={[{ rel: 'canonical', href: `${CANONICAL_URL}/store` }]}
          meta={[
            {
              name: 'description',
              content:
                'Browse the Babylist shop to discover the best baby gear tested by experts and real parents. Get anything you need for the baby in your life or start a registry.',
            },
          ]}
          title="Baby Clothes, Gear & More - Babylist Shop"
        />
        <RenderBanner
          currentUser={currentUser}
          openDiscountModal={openDiscountModal}
        />
        <div className="container">
          <StoreHero />
          <ContentContainer
            storeNavigationCategories={storeNavigationCategories}
          >
            {productCollections &&
              productCollections.map(
                ({ id: collectionId, title, teaser, products }) => (
                  <section
                    className={css.collectionContainer}
                    key={collectionId}
                  >
                    <ProductCollection
                      id={collectionId}
                      key={collectionId}
                      maxVisibleProducts={null}
                      products={products.map(
                        ({
                          brand,
                          id,
                          image: { url: imageUrl },
                          name,
                          path: productUrl,
                          price,
                          rating,
                          reviewCount,
                        }) => ({
                          brand,
                          id,
                          imageUrl,
                          name,
                          price,
                          productUrl,
                          rating,
                          reviewCount,
                        })
                      )}
                      teaser={teaser}
                      title={title}
                    />
                  </section>
                )
              )}

            <section>
              <SubHeading className="text-bold h3 ptn mtn">
                Categories
              </SubHeading>
              <CategoryGrid categories={storeRootCategories} />
            </section>

            <WhyShopBabylist />
            <Row className="mvxl">
              <Col sm={6}>
                <CreateBabylistCTA />
              </Col>
              <Col sm={6}>
                <FindBabylistCTA />
              </Col>
            </Row>
            <Disclaimers />
          </ContentContainer>
        </div>
      </div>
      <RegistryDiscountStatusModal
        show={isRegistryDiscountModalOpen}
        onHide={handleDiscountModalClose}
        registryDiscount={registryDiscount}
      />
    </NiceModal.Provider>
  )
}

export default LandingPage
